import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { JSX } from "react";

import { STEER_TARGET_LABELS, steerTarget } from "@xop/dashboard-shared";

import { useCan } from "../../../auth/permissions.js";
import { salesApi } from "../client.js";
import { SteeringNoteInput, type SteeringSeed } from "./SteeringNoteInput.js";

/**
 * Header button + dialog for sending a steering note to the agent.
 *
 * The steering form was previously inline on the Today tab. Moving it
 * into a header-mounted dialog has two wins:
 *
 *   1. Steering becomes a top-level operator action available from
 *      every Sales tab (Today, Leads, Strategy, Knowledge, Brain),
 *      not just one. The CEO browsing leads can drop a steering note
 *      without navigating away.
 *   2. The Today tab loses a tall freeform textarea that competed
 *      visually with the leads + status above it.
 *
 * The actual form is still `<SteeringNoteInput>` (unchanged) — we just
 * mount it inside a Dialog. The component is self-contained
 * (uses `salesApi.postSteeringNote` + invalidates the Today query) so
 * dropping it into a dialog needs no other plumbing.
 *
 * Hidden entirely for non-`sales:edit` viewers (matches the same gate
 * SteeringNoteInput already enforces — no point rendering an inert
 * button just to have it disappear when clicked).
 */
/** Monotonic so seeding the same note twice still refills the box. */
let seedNonce = 1;

export function SteerAgentButton(): JSX.Element | null {
  const canEdit = useCan("sales:edit");
  const [open, setOpen] = useState(false);
  // "You recently told the agent" — the steering history, moved off the Today
  // page to here. Fetched only while the dialog is open; reuses the cached
  // Today query so it's free when Today is already mounted.
  const today = useQuery({
    queryKey: ["sales", "today"],
    queryFn: salesApi.getToday,
    enabled: open,
  });
  const recent = today.data?.recent_steering_notes ?? [];
  // Re-send an older note through the box (see `SteeringSeed`).
  const [seed, setSeed] = useState<SteeringSeed | undefined>(undefined);

  if (!canEdit) return null;

  return (
    <>
      <Button
        type="button"
        variant="outlined"
        color="inherit"
        size="small"
        onClick={() => setOpen(true)}
        // Box the emoji to a 16px square (like the sibling Pause/Resume
        // button's 16px MUI icon) so its taller glyph metrics can't inflate
        // the button height.
        startIcon={
          <Box
            component="span"
            aria-hidden
            sx={{ display: "inline-flex", fontSize: 16, lineHeight: 1 }}
          >
            🎯
          </Box>
        }
        title="Drop a freeform note for the agent — it becomes a standing directive it follows until you archive it"
        aria-label="Steer the agent"
        // Height-match the contained Pause/Resume button (AgentControlButton,
        // also py:0.75): this outlined variant adds a 1px border top+bottom
        // that the flat py override doesn't compensate for (MUI's default
        // outlined padding would), so trim 1px off each side (6→5px) → equal
        // outer height.
        sx={{ fontSize: 12, fontWeight: 600, px: 1.5, py: 0.625 }}
      >
        Steer the agent
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, pr: 1 }}
        >
          Steer the agent
          <IconButton
            aria-label="Close"
            title="Close"
            onClick={() => setOpen(false)}
            size="small"
            sx={{ color: "text.secondary", "&:hover": { color: "text.primary" } }}
          >
            <Box component="span" aria-hidden sx={{ fontSize: 18, lineHeight: 1 }}>
              ✕
            </Box>
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            Pick which agent to steer, then drop a note. For the{" "}
            <strong>Lead Generator</strong> it becomes a standing directive it
            keeps steering toward (missions, strategy, lead hunting) until you
            archive it from the "Active directives" panel on Status. For the{" "}
            <strong>Harvester</strong> it changes what it hunts, by editing its
            live campaign — you'll see exactly what it will change before you
            send.
          </DialogContentText>
          {/*
            SteeringNoteInput already renders its own headed card. We
            tuck it inside the dialog body — it brings its own form,
            char counter, send button, and success flash; nothing else
            to wire here.
          */}
          <SteeringNoteInput seed={seed} />
          {recent.length > 0 ? (
            <Box sx={{ mt: 2.5 }}>
              <Typography
                component="h3"
                sx={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled", mb: 1 }}
              >
                You recently told the agent
              </Typography>
              <Box component="ul" sx={{ display: "grid", gap: 1.25, listStyle: "none", m: 0, p: 0, maxHeight: 220, overflowY: "auto" }}>
                {recent.map((n) => {
                  const target = steerTarget(n.target);
                  const harvester = target === "harvester" || target === "both";
                  // A harvester note with no campaign never reached the
                  // sweep. Every note written before 2026-08-07 is in that
                  // state — back then it was read as a matrix constraint, and
                  // a phrasing the vocabulary didn't recognise was silently
                  // discarded. That is exactly the note the operator wants to
                  // re-send, so offer it rather than leaving them to retype it.
                  const unapplied = harvester && !n.applied_campaign;
                  return (
                    <Box component="li" key={n.id}>
                      <Box component="span" sx={{ fontSize: 11, color: "text.disabled" }}>
                        {new Date(n.created_at).toLocaleString()} · {STEER_TARGET_LABELS[target]} ·{" "}
                        {n.applied_campaign
                          ? `✓ applied to ${n.applied_campaign}`
                          : n.consumed_at
                            ? "✓ used"
                            : harvester
                              ? "never applied"
                              : "queued"}
                      </Box>
                      <Typography sx={{ fontSize: 13, color: "text.primary" }}>{n.body}</Typography>
                      {unapplied ? (
                        <Button
                          type="button"
                          size="small"
                          variant="text"
                          onClick={() =>
                            setSeed({ body: n.body, target, at: seedNonce++ })
                          }
                          sx={{ fontSize: 11, px: 0, minWidth: 0, textTransform: "none" }}
                        >
                          Apply to campaign →
                        </Button>
                      ) : null}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
